import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { classifyContributor } from "@/lib/api";
import ContributorClassTag from "@/components/ContributorClassTag";
import ToolLayout from "@/components/ToolLayout";
import PostResultCTA from "@/components/PostResultCTA";
import useSEO from "@/hooks/useSEO";

const SOURCE_TYPES = [
  "Zendesk",
  "Salesforce",
  "Confluence",
  "Slack",
  "Microsoft Teams",
  "Email (internal)",
  "CRM (other)",
  "Internal knowledge base",
  "Customer interviews",
  "Third-party content",
  "Other",
];

const DOMAINS = [
  "Customer Support",
  "Finance / Payments",
  "HR / People Ops",
  "Legal / Compliance",
  "Sales",
  "Product / Engineering",
  "Other",
];

const CLASS_INFO = {
  "1": {
    label: "Class 1",
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    blurb: "Lowest scrutiny required. Clear ownership and direct authorship.",
  },
  "2": {
    label: "Class 2",
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    blurb: "Evidence chain required. Patterns derived from agent actions, not explicit authorship.",
  },
  "3": {
    label: "Class 3",
    color: "text-rose-700",
    bg: "bg-rose-50 border-rose-200",
    blurb: "Highest scrutiny. Implicit or inferred patterns — provenance is indirect or uncertain.",
  },
};

export default function ContributorClassifier() {
  useSEO({
    title: "Contributor Class Classifier — Free Tool — ProofMark",
    description:
      "Describe a data source and get an instant Class 1, 2, or 3 classification with review requirements and policy flags. Free AI governance tool.",
    path: "/tools/contributor-class-classifier",
  });

  const [description, setDescription] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await classifyContributor(
        description,
        sourceType || undefined,
        domain || undefined
      );
      setResult(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 503) {
        setError("AI features are not available in this environment. Configure ANTHROPIC_API_KEY to enable classification.");
      } else {
        setError(detail || "Classification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const cls = result ? CLASS_INFO[result.contributor_class] : null;

  return (
    <ToolLayout>
      <div className="mb-8">
        <span className="tracked-caps">Free Tool</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-3">Contributor Class Classifier</h1>
        <p className="text-muted-foreground text-lg max-w-2xl">
          Describe a data source and get an instant Class 1, 2, or 3 classification — with
          reasoning, review requirements, and policy flags to check before using it in an AI system.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Describe the data source <span className="text-rose-500">*</span>
            </label>
            <textarea
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Zendesk support tickets from the payments team, covering refund and chargeback resolution over the past 18 months. Approximately 4,500 resolved tickets."
              rows={5}
              className="w-full rounded-md border border-border bg-card px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">Source system (optional)</label>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              className="w-full rounded-md border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
            >
              <option value="">Select system…</option>
              {SOURCE_TYPES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">Business domain (optional)</label>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full rounded-md border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
            >
              <option value="">Select domain…</option>
              {DOMAINS.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading || !description.trim()}
            className="w-full bg-brand text-brand-foreground py-2.5 rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Classifying…
              </>
            ) : (
              "Classify source →"
            )}
          </button>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </form>

        {/* Result */}
        <div>
          {result && cls ? (
            <div className={`border rounded-lg p-6 h-full ${cls.bg}`}>
              <span className="tracked-caps mb-3 block">Classification result</span>

              <div className="flex items-center gap-3 mb-4">
                <ContributorClassTag
                  classes={[`Class ${result.contributor_class}`]}
                  count={null}
                />
                <span className={`text-lg font-medium ${cls.color}`}>{cls.label}</span>
              </div>

              <p className={`text-sm mb-5 ${cls.color}`}>{cls.blurb}</p>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Reasoning
                  </p>
                  <p className="text-sm leading-relaxed">{result.reasoning}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Review requirements
                  </p>
                  <p className="text-sm leading-relaxed">{result.review_requirements}</p>
                </div>
                {result.policy_flags && result.policy_flags.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                      Policy flags to check
                    </p>
                    <ul className="space-y-1">
                      {result.policy_flags.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-5 border-t border-current/10">
                <PostResultCTA
                  openLoop="Every source in your stack has a class — most teams have never mapped them. ProofMark classifies your full intake automatically and gates review by class."
                  source="contributor-classifier"
                  nextPath="/tools/policy-checker"
                  nextLabel="Next: check an artifact for policy issues →"
                  testimonial={null}
                />
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border rounded-lg p-6 h-full flex items-center justify-center text-center">
              <div>
                <p className="text-muted-foreground text-sm mb-2">
                  Your classification result will appear here
                </p>
                <p className="text-xs text-muted-foreground/60">
                  Powered by Claude — ProofMark AI
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Educational content */}
      <div className="mt-20 space-y-10 border-t border-border/80 pt-12">
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            What are contributor classes in enterprise AI knowledge?
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            When knowledge enters an AI system — whether as a training signal, retrieval source, or
            behavioural rule — the provenance of that knowledge determines how carefully it needs to
            be reviewed. Contributor classes are a structured way to express that provenance. A Class
            1 source (a company-owned policy document) has clear authorship. A Class 3 source
            (patterns inferred from aggregated behaviour) may carry implicit biases, attribution
            issues, or PII that are not immediately obvious.
          </p>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">Class 1, 2, and 3 explained</h2>
          <div className="space-y-4 max-w-3xl">
            {Object.entries(CLASS_INFO).map(([k, v]) => (
              <div key={k} className={`border rounded-lg p-4 ${v.bg}`}>
                <p className={`font-medium ${v.color} mb-1`}>{v.label}</p>
                <p className="text-sm text-muted-foreground">{v.blurb}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            How contributor class affects your review workflow
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            Class determines the review gates an artifact must pass before deployment. Class 1
            artifacts may go through a single-reviewer approval. Class 2 artifacts require an
            evidence chain linking extracted patterns to specific source records. Class 3 artifacts
            typically require a compliance review and explicit policy sign-off before any AI system
            can use them. Getting the classification right at intake prevents bottlenecks — and
            revocations — downstream.
          </p>
        </section>
      </div>
    </ToolLayout>
  );
}
